import type { ReactNode } from 'react';
import React from 'react';
import type { StyledProps } from '@compiled/react';
import { styled, css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { BREADCRUMBS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	BreadcrumbsWithSpaceNameDropdown,
	BREADCRUMBS_MODES,
} from './BreadcrumbsWithSpaceNameDropdown.tsx';

const hideDuringPdfPrint = css({
	'@media print': {
		display: 'none',
	},
});

const CompanyHubBreadcrumbs = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CompanyHubBreadcrumbs" */ './company-hub/CompanyHubBreadcrumbs.tsx'
			)
		).CompanyHubBreadcrumbs,
});

// Keep this in sync with entry-points/breadcrumbsConstants.ts
const BREADCRUMB_HEADER_HEIGHT = 56;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FixedSpaceHeaderContainer: React.ComponentType<
	{
		hasStackedBanner?: boolean;
		isThemed?: boolean;
		isTransparent?: boolean;
		justifyContent?: 'space-between' | 'flex-end';
		shouldHavePadding?: boolean;
		shouldGrowForStackedBanner?: boolean;
	} & React.ClassAttributes<HTMLDivElement> &
		React.HTMLAttributes<HTMLDivElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div<{
	hasStackedBanner?: boolean;
	isThemed?: boolean;
	isTransparent?: boolean;
	justifyContent?: 'space-between' | 'flex-end';
	shouldHavePadding?: boolean;
	shouldGrowForStackedBanner?: boolean;
}>({
	// Only grows for a banner that is stacked above the header row; the create banner stacked below
	// it keeps the row height.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (p) => (p.shouldGrowForStackedBanner ? 'auto' : `${BREADCRUMB_HEADER_HEIGHT}px`),
	// needs to be above 10 so it covers floating sticky table rows
	// also headers are not fixed for themed spaces

	zIndex: 12,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth:
		'calc( 100vw - var(--leftPanelWidth, 0px) - var(--leftSidebarWidth, 0px) - var(--rightPanelWidth, 0px) - var(--rightSidebarWidth, 0px) )',
	borderBottom: 'none',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	position: (p) => (p.isThemed ? 'sticky' : 'fixed'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: (p) => (p.isThemed || !p.shouldHavePadding ? '0px' : '0 12px'),
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: (p) => (p.hasStackedBanner ? 'column' : 'row'),
	alignItems: 'center',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (p) => (p.isTransparent ? 'transparent' : token('elevation.surface')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	left: 'var(--leftSidebarWidth)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: (p) => p.justifyContent || 'space-between', // justifyContent is used for company hub specifically
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SpaceHeaderWithBannerWrapper = styled.div<{
	isThemed?: boolean;
	isFullWidth?: boolean;
	isTransparent?: boolean;
}>({
	// needs to be above 10 so it covers floating sticky table rows
	// also headers are not fixed for themed spaces
	zIndex: 12,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: (p) =>
		p.isFullWidth
			? 'none'
			: 'calc( 100vw - var(--leftPanelWidth, 0px) - var(--leftSidebarWidth, 0px) - var(--rightPanelWidth, 0px) - var(--rightSidebarWidth, 0px) )',
	borderBottom: 'none',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	position: (p) => (p.isThemed ? 'sticky' : 'fixed'),
	display: 'flex',

	flexDirection: 'column',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (p) => (p.isTransparent ? 'transparent' : token('elevation.surface')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	left: 'var(--leftSidebarWidth)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: '0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SpaceHeaderWrapper = styled.div<{
	isThemed?: boolean;
	shouldHavePadding?: boolean;
}>({
	height: `${BREADCRUMB_HEADER_HEIGHT}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: (p) => (p.isThemed || !p.shouldHavePadding ? '0px' : '0 12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	gap: token('space.025'),
	display: 'flex',
	alignItems: 'center',
});

export type BreadcrumbsProps = {
	contentId?: string;
	contentTitle?: string /** Show an extra breadcrumb item when it is provided. This is used in sticky content header where we want to display the title of the current page.**/;
	spaceKey?: string;
	testId?: string;
	showInlineRename?: boolean;
	shouldDisableMoveButton?: boolean;
	displayMode?: BREADCRUMBS_MODES;
	contentLink?: string /** if a content link value is provided, allow the content title portion to link to that * (used for views like page analytics and version history) */;
	contentType?: string;
	subType?: string;
};

export function Breadcrumbs({
	contentId,
	contentTitle,
	spaceKey,
	testId,
	showInlineRename,
	shouldDisableMoveButton,
	displayMode = BREADCRUMBS_MODES.FULL,
	contentLink,
	contentType,
	subType,
}: BreadcrumbsProps): React.JSX.Element | null {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!spaceKey) {
		return null;
	}
	if (isCompanyHubSpaceKey(spaceKey)) {
		return <CompanyHubBreadcrumbs />;
	}

	const fireBreadcrumbError = (error: Error) => {
		let err: string | undefined;
		let nativeError: string | undefined;
		try {
			err = JSON.stringify(error, Object.getOwnPropertyNames(error)); // https://stackoverflow.com/a/26199752
			nativeError = JSON.stringify(error['nativeError']);
		} catch {
			// if no error, means it failed to stringify so just set as empty string
			if (!err) err = '';
			if (!nativeError) nativeError = '';
		} finally {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'breadcrumbs',
					action: 'errored',
					actionSubject: 'viewPageBreadcrumbs',
					attributes: {
						message: error.message,
						error: err,
						nativeError,
					},
				},
			}).fire();
		}
	};

	return (
		<ErrorBoundary attribution={Attribution.DISCO}>
			<GenericErrorBoundary
				attribution={Attribution.DISCO}
				onError={fireBreadcrumbError}
				renderOnError={() => null}
			>
				<ExperienceStart name={BREADCRUMBS_EXPERIENCE} />
				<BreadcrumbsWithSpaceNameDropdown
					contentTitle={contentTitle}
					spaceKey={spaceKey || ''}
					contentId={contentId}
					testId={testId}
					showInlineRename={showInlineRename}
					shouldDisableMoveButton={shouldDisableMoveButton}
					displayMode={displayMode}
					contentLink={contentLink}
					contentType={contentType}
					subType={subType}
				/>
			</GenericErrorBoundary>
		</ErrorBoundary>
	);
}

export const FixedBreadcrumbsNavForPageLayout = (): React.JSX.Element => {
	const [spaceKey] = usePageSpaceKey();
	return (
		<FixedSpaceHeaderContainer>
			<Breadcrumbs spaceKey={spaceKey} />
		</FixedSpaceHeaderContainer>
	);
};

export const FixedSpaceHeaderWithBannerContainer = ({
	children,
	banner,
	isThemed,
	shouldHavePadding,
	isFullWidth,
	isTransparent,
	testId,
}: {
	children: ReactNode;
	banner: ReactNode;
	isThemed: boolean;
	shouldHavePadding?: boolean;
	isFullWidth?: boolean;
	isTransparent?: boolean;
	testId?: string;
}): React.JSX.Element => {
	return (
		<SpaceHeaderWithBannerWrapper
			isThemed={isThemed}
			isFullWidth={isFullWidth}
			isTransparent={isTransparent}
			css={[
				expValEquals('afm-hide-elements-from-pdf-print', 'isEnabled', true) && hideDuringPdfPrint,
			]}
		>
			{banner}
			<SpaceHeaderWrapper
				isThemed={isThemed}
				data-testid={testId}
				shouldHavePadding={shouldHavePadding}
			>
				{children}
			</SpaceHeaderWrapper>
		</SpaceHeaderWithBannerWrapper>
	);
};
